import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { createContainer } from 'unstated-next';

import { LOGIN, ZOOM_LOGIN_REDIRECT_PATH } from '../../config/apiPaths';
import useLocalStorage from '../../hooks/useLocalStorage';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { OrganizationType, useSchoolInfo } from '../../hooks/useSchoolInfo';
import { useSsoConfig } from '../../hooks/useSsoConfig';
import { type ClassResponse, getClassMeeting } from '../../utils/getClassMeeting';
import { createOauthState, parseOauthState } from '../../utils/oauthState';
import { getUrlParams, getUrls } from '../../utils/url';

export const ERROR_AUTH_INVALID_CREDENTIALS = 1001;
export const ERROR_AUTH_BAD_REQUEST = 1002;
export const ERROR_AUTH_SERVER_ERROR = 1003;
export const ERROR_AUTH_NO_RESPONSE = 1004;
export const ERROR_AUTH_UNKNOWN = 1005;

const useLoginContainer = () => {
    const { api } = getUrls();
    const history = useHistory();

    const { loading: schoolLoading, error: schoolError, data: school } = useSchoolInfo();

    const { loading: ssoConfigLoading, error: ssoConfigError, data: ssoConfig } = useSsoConfig();

    const { loading: orgSettingsLoading, error: orgSettingsError, data: orgSettings } = useOrgSettings();

    const [remember] = useLocalStorage<boolean>('cfz_auth_remember', false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authError, setAuthError] = useState<number | null>(null);
    const [isAutoSignIn, setIsAutoSignIn] = useState(true);
    const [isOriginMeeting, setIsOriginMeeting] = useState(false);
    const [meetingData, setMeetingData] =
        useState<{
            className: ClassResponse['class_name'];
            classTimes: ClassResponse['data']['class_times'];
        } | null>(null);

    useEffect(() => {
        const fetchMeetingData = async () => {
            const state = parseOauthState();
            if (!state || !state.redirect) return;
            const url = new URL(state.redirect);
            if (!url.pathname.includes('meeting/')) {
                return;
            }

            setIsOriginMeeting(true);

            const classUuid = url.searchParams.get('class_uuid');

            if (!classUuid) return;

            try {
                const response = await getClassMeeting(classUuid);
                setMeetingData({
                    className: response.class_name,
                    classTimes: response.data.class_times
                });
            } catch (e) {
                console.error(e);
            }
        };

        fetchMeetingData();
    }, []);

    /**
     * Forward OAuth state to the React auth handler
     */
    const redirect = useCallback(
        (state) => {
            delete state.temp_session_id;

            const params = new URLSearchParams({
                state: JSON.stringify(state)
            });

            history.push({
                pathname: 'auth',
                search: params.toString()
            });
        },
        [history]
    );

    const backToStateUrl = useCallback(() => {
        const state = parseOauthState();
        redirect(state);
    }, [redirect]);

    useEffect(() => {
        const fetchMeetingData = async () => {
            const state = parseOauthState();
            if (!state || !state.redirect) return;
            const url = new URL(state.redirect);
            if (!url.pathname.includes('meeting/')) {
                return;
            }

            setIsOriginMeeting(true);
        };

        fetchMeetingData();
    }, []);

    /**
     * Shows errors based on error codes from OAuth
     * sign-in attempts.
     */
    useEffect(() => {
        const { error } = getUrlParams();

        if (error && !Number.isNaN(Number(error))) {
            setAuthError(Number(error));
        }
    }, []);

    /**
     * Automatically redirect users to Zoom OAuth without
     * the option to sign in with email and password.
     */
    useEffect(() => {
        if (schoolLoading) return;
        if (school?.type === OrganizationType.User) {
            setIsAutoSignIn(true);

            const params = new URLSearchParams({
                state: JSON.stringify(parseOauthState() || createOauthState())
            });

            window.location.href = `${api}${ZOOM_LOGIN_REDIRECT_PATH}?${params.toString()}`;
        }
    }, [api, history, school?.type, schoolLoading]);

    /**
     * Auto redirect a user if they've already got a token,
     * and they previously selected "remember me".
     */
    useEffect(() => {
        if (isAuthenticating) return;

        const token = localStorage.getItem('user_uuid');

        if (!token || !remember) {
            setIsAutoSignIn(false);
            return;
        }

        redirect(createOauthState({ ...parseOauthState() }));
    }, [history, isAuthenticating, redirect, remember]);

    /**
     * Initiate the login process and redirect the user
     * according to the redirect url param.
     */
    const login = useCallback(
        ({ email, password }) => {
            const authenticate = async () => {
                setIsAuthenticating(true);
                setAuthError(null);

                try {
                    const response = await axios.post(`${api}${LOGIN}`, { email, password });

                    const newState = createOauthState({ ...parseOauthState(), user_uuid: response.data.token });

                    redirect(newState);
                } catch (err: any) {
                    if (err.response) {
                        switch (err.response.status) {
                            case 400:
                                setAuthError(ERROR_AUTH_BAD_REQUEST);
                                break;
                            case 401:
                                setAuthError(ERROR_AUTH_INVALID_CREDENTIALS);
                                break;
                            default:
                                setAuthError(ERROR_AUTH_SERVER_ERROR);
                        }

                        console.error(`Request failed with status: ${err.response.status}.`);
                        console.error(err.response.data);
                    } else if (err.request) {
                        setAuthError(ERROR_AUTH_NO_RESPONSE);
                        console.error(err.request);
                    } else {
                        setAuthError(ERROR_AUTH_UNKNOWN);
                        console.error(err);
                    }

                    setIsAuthenticating(false);
                }
            };

            authenticate();
        },
        [api, redirect]
    );

    return {
        isLoading: schoolLoading || ssoConfigLoading || orgSettingsLoading,
        error: schoolError || ssoConfigError || orgSettingsError,
        school,
        ssoConfig,
        orgSettings,
        login,
        isAuthenticating,
        authError,
        isAutoSignIn,
        isOriginMeeting,
        backToStateUrl,
        meetingData
    };
};

const LoginContainer = createContainer(useLoginContainer);

export default LoginContainer;
